import type { Environment } from '~/engine/data/Environment';
import { isSelectedStructurePeeked } from '~/engine/view/layers/contourAlpha';
import { localization } from '~/i18n';

/** The part of a view {@link isViewOfEnvironment} reads. */
export interface EnvironmentViewLike {
    readonly environment: Environment | undefined;
}

/**
 * Whether a write armed on `environment` may act through `view`: the view
 * shows that Environment. A tool armed with no Environment acts through no
 * view, so an unresolved target refuses every press (fail closed).
 */
export function isViewOfEnvironment(
    view: EnvironmentViewLike,
    environment: Environment | undefined
): boolean {
    return environment !== undefined && view.environment === environment;
}

/**
 * The reason a write tool armed on `environment` refuses a left press on
 * `view`, or undefined when it accepts the press.
 *
 * The environment is checked before the plane: a view of another
 * Environment, or any view when none is armed (fail closed), is refused
 * with the `contouring.refused.tileNotEditable` reason whatever its plane.
 * So is any view while the selected structure is peeked. Only a view of
 * `environment` is given to `planeRefusal`, the tool's own rule for the
 * planes it cannot write through.
 */
export function writeRefusal<V extends EnvironmentViewLike>(
    view: V,
    environment: Environment | undefined,
    planeRefusal?: (view: V) => string | undefined
): string | undefined {
    if (!isViewOfEnvironment(view, environment)) {
        return localization.t('contouring.refused.tileNotEditable');
    }
    // A write edits the selected structure: while it is peeked its contour
    // is not drawn, and the edit would land where the user cannot see it.
    if (
        isSelectedStructurePeeked(
            environment?.structureSet?.selectedStructure?.roiNumber,
            environment?.peekedRoiNumber
        )
    ) {
        return localization.t('contouring.refused.structurePeeked');
    }
    return planeRefusal?.(view);
}
